/**
 * English (UK) locale for devices-ui.
 * Export is the "devices" subtree: host apps merge this into their i18n
 * resources as translation.devices so that t('devices.list.heading') etc. resolve.
 */
import type { DevicesLocale } from './en'

export const devicesEnGb = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `Cancel`,
    delete: `Delete`,
    save: `Save`,
    saveAndReboot: `Save & Reboot`,
    actions: `Actions`,
    error: `Error`,
    yes: `Yes`,
    no: `No`,
    all: `All`,
    unchanged: `Unchanged`,
    loading: `Loading...`,
    none: `— None —`,
    backToList: `Back to list`,
  },

  // ---------------------------------------------------------------------------
  // Options used across multiple components
  // ---------------------------------------------------------------------------
  options: {
    transportUdp: `UDP`,
    transportTcp: `TCP`,
    transportTls: `TLS`,
    jitterNormal: `Normal`,
    jitterLow: `Low`,
    jitterHigh: `High`,
    natNormal: `Normal`,
    natStun: `STUN`,
    serverGroupPrimary: `Primary`,
    serverGroupSecondary: `Secondary`,
    sourceMango: `Mango New`,
    sourceRefurbished: `Mango Refurbished`,
    sourceCustomer: `Customer`,
    wifiSecurityNone: `NONE`,
    wifiSecurityWep: `WEP`,
    wifiSecurityWpa: `WPA/WPA2 PSK`,
    wifiSecurityWpa3: `WPA3-Personal`,
    timezoneAlaska: `Alaska`,
    timezoneArizona: `Arizona`,
    timezoneAtlantic: `Atlantic Time (Canada)`,
    timezoneCentral: `Central Time (US & Canada)`,
    timezoneEastern: `Eastern Time (US & Canada)`,
    timezoneGreenwich: `Greenwich Mean Time (UK)`,
    timezoneHawaii: `Hawaii`,
    timezoneIndiana: `Indiana (East)`,
    timezoneMountain: `Mountain Time (US & Canada)`,
    timezonePacific: `Pacific Time (US & Canada)`,
    timezonePuertoRico: `Puerto Rico`,
  },

  // ---------------------------------------------------------------------------
  // Device form (add / edit)
  // ---------------------------------------------------------------------------
  form: {
    addDevice: `Add device`,
    editDevice: `Edit device`,
    createDevice: `Create device`,
    loadingDevice: `Loading device...`,

    // Section headers
    deviceInfo: `Device Info`,
    advanced: `Advanced`,
    displaySettings: `Display Settings`,

    // Field labels
    model: `Model`,
    selectModel: `Select device model…`,
    mostCommonYealink: `Most Common (Yealink)`,
    macAddress: `MAC Address`,
    macPlaceholder: `AA:BB:CC:DD:EE:FF`,
    generateRandomMac: `Generate random MAC address`,
    generateRandomMacConfirmTitle: `Replace MAC address?`,
    generateRandomMacConfirmDescription: `Generating a random MAC address will replace the current value. Are you sure you want to continue?`,
    generateRandomMacConfirmAction: `Generate new MAC`,
    copyProvisioningMac: `Copy MAC for provisioning URL`,
    copyProvisioningMacHint: `Copy as lowercase, no colons (for provisioning URL)`,
    copyProvisioningMacCopied: `Copied {{value}}`,
    copyProvisioningMacFailed: `Copy failed. Select the value manually instead.`,
    checkingUniqueness: `Checking uniqueness…`,
    macRequired: `MAC address is required`,
    macInvalid: `MAC address is invalid`,
    macAlreadyInUse: `MAC address already in use`,
    modelRequired: `Device model is required.`,
    warrantyExpiresAt: `Warranty Expires At`,
    source: `Source`,
    lastProvisionedTime: `Last Provisioned Time`,
    lastProvisionedFrom: `Last Provisioned From`,
    assignedUser: `Assigned User`,
    assignedUserRequired: `Assigned user is required.`,
    directory: `Directory`,
    directoryRequired: `Directory is required.`,
    selectDirectory: `Select directory...`,
    primaryLineExtension: `Primary Line Extension`,
    noExtensionsForUser: `The assigned user does not have any valid line extensions. Select a different user or assign a line extension before saving.`,
    primaryLineExtensionRequired: `Primary line extension is required.`,
    timeZone: `Time Zone`,
    stationName: `Station Name`,
    stationNameRequired: `Station name is required.`,
    stationNamePlaceholder: `e.g. Front desk`,
    enableDaylightSavings: `Enable Daylight Savings`,
    cannotSaveTitle: `Cannot save yet`,
    buttonConfigurationInvalid: `Fix the invalid button configuration before saving.`,

    // Advanced section labels
    pages: `Pages`,
    lineKeysAvailable: `Line Keys Available`,
    selectModelFirst: `Select Model First...`,
    lineKeysHelper: `({{pages}} page{{pageSuffix}} x {{keysPerPage}} keys = {{total}} total)`,
    allowProvisioning: `Allow Provisioning`,
    sidecarEnabled: `Sidecar Enabled`,
    transport: `Transport`,
    jitter: `Jitter`,
    serverGroupHost: `Server Group Host`,
    natTraversal: `NAT Traversal`,
    wifiEnabled: `WiFi Enabled`,
    wifiNetworkSsid: `WiFi Network SSID`,
    wifiSecurityMode: `WiFi Security Mode`,
    wifiPassword: `WiFi Password`,
    acousticShieldEnabled: `Acoustic Shield Enabled`,
  },

  // ---------------------------------------------------------------------------
  // Device preview card
  // ---------------------------------------------------------------------------
  preview: {
    title: `Device Preview`,
    unavailable: `Preview unavailable for selected model.`,
    emptyAlt: `Empty device preview`,
  },

  // ---------------------------------------------------------------------------
  // Button grid
  // ---------------------------------------------------------------------------
  buttons: {
    linesAndButtons: `Lines & Buttons`,
    sidecarButtons: `Sidecar Buttons`,
    lineButtonHelper_one: `{{count}} button slot for {{modelName}}. Row 1 is always Line and follows Primary Line Extension.`,

    lineButtonHelper_other: `{{count}} button slots for {{modelName}}. Row 1 is always Line and follows Primary Line Extension.`,
    sidecarButtonHelper_one: `{{count}} sidecar slot.`,

    sidecarButtonHelper_other: `{{count}} sidecar slots.`,
    noSlotsForModel: `No button slots available for this model.`,
    selectModelForSlots: `Select a device model to see button slots.`,
    columnNumber: `#`,
    columnFunction: `Function`,
    columnValue: `Value`,
    columnLabel: `Label`,
    phoneNumberPlaceholder: `Phone number`,
    optionalLabelPlaceholder: `Optional label`,
    extensionNone: `-- None --`,

    // Button function options
    functionBlank: `Blank`,
    functionLine: `Line`,
    functionRollover: `Rollover`,
    functionSpeedDial: `Speed Dial`,
    functionPresence: `BLF / Presence`,
    functionParkingLot: `Parking Lot`,
    functionSharedVoicemail: `Shared Voicemail`,

    // Preset speed dial options
    presetMangoSupport: `Mango Support`,
    presetLoginQueue: `Login Queue`,
    presetLogoutQueue: `Logout Queue`,
    presetBlockCaller: `Block Caller`,
    presetOpenClose: `Open/Close`,
    presetPauseResumeRecording: `Pause/Resume Recording`,
    presetAlternateVm: `Alternate VM`,
    preset911History: `911 History`,

    // Preset button labels
    presetLabelMangoSupport: `Mango Support`,
    presetLabelLoginQ: `Login Q`,
    presetLabelLogoutQ: `Logout Q`,
    presetLabelBlock: `Block`,
    presetLabelOpenClose: `Open/Close`,
    presetLabelPauseResume: `Pause/Resume`,
    presetLabelAltVm: `Alt VM`,
    presetLabel911Hist: `911 Hist`,

    parkingLotOption: `Parking Lot {{number}}`,

    multipleLineWarningTitle: `Multiple Line buttons detected`,
    multipleLineWarning: `Adding multiple Line buttons may cause undesired results. Make sure the extension you select is not assigned to another Line button and is not configured on any other device.`,
    multipleLineWarningProceed: `Proceed`,
    multipleLineWarningCancel: `Cancel`,
  },

  // ---------------------------------------------------------------------------
  // Clone dialog
  // ---------------------------------------------------------------------------
  clone: {
    title: `Clone device`,
    description: `Create a new device from "{{sourceLabel}}". Enter a unique MAC address. All settings and buttons will be copied to the new device.`,
    loadingSettings: `Loading device settings…`,
    stationLabelPlaceholder: `Clone of device`,
    cloneButton: `Clone device`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `Delete device`,
    confirmation: `Are you sure you want to delete "{{name}}"? This action cannot be undone.`,
    unnamedDevice: `Unnamed device`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `Mass Edit Devices`,
    description_one: `Updating {{count}} device. Leave a field unchanged to keep current value.`,

    description_other: `Updating {{count}} devices. Leave a field unchanged to keep current value.`,
    transport: `Transport`,
    natTraversal: `NAT Traversal`,
    jitter: `Jitter`,
    timeZone: `Time Zone`,
    daylightSaving: `Daylight Saving`,
    allowProvisioning: `Allow Provisioning`,
    serverGroupHost: `Server Group Host`,
    saveButton_one: `Save {{count}} device`,

    saveButton_other: `Save {{count}} devices`,
  },

  // ---------------------------------------------------------------------------
  // Set desired firmware dialog
  // ---------------------------------------------------------------------------
  setFirmware: {
    title: `Set desired firmware version`,
    description: `Set the desired firmware version for {{count}} selected device(s). Configuration will be reloaded after the update.`,
    label: `Desired firmware version`,
    placeholder: `e.g. 1.2.3.4`,
    confirmButton: `Set and reload`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    heading: `Devices`,
    searchPlaceholder: `Search by name, user, extension, or MAC...`,
    addDevice: `Add device`,
    actionsButton: `Actions`,
    actionsWithCount: `Actions ({{count}} selected)`,
    massEditAction: `Mass Edit ({{count}})`,
    reloadConfigAction: `Reload configuration ({{count}})`,
    reloadConfigRebootAction: `Reload config & reboot ({{count}})`,
    setFirmwareAction: `Set desired firmware ({{count}})`,
    rebuildAll: `Rebuild All`,
    rebuildAndReboot: `Rebuild & Reboot`,
    rebuildAllConfirmTitle: `Rebuild all device configs?`,
    rebuildAllConfirmDescription: `You are about to rebuild the configuration for every device on this PBX. Are you sure you want to continue?`,
    rebuildAllConfirmAction: `Rebuild all`,
    rebuildAndRebootConfirmTitle: `Rebuild and reboot all devices?`,
    rebuildAndRebootConfirmDescription: `You are about to rebuild the configuration for every device on this PBX and reboot them. Are you sure you want to continue?`,
    rebuildAndRebootConfirmAction: `Rebuild & reboot`,
    pbxRequired: `PBX is required for the Devices page.`,
    failedToLoad: `Failed to load devices`,
    noMatchingDevices: `No devices match your search criteria`,

    // Column headers
    columnName: `Name`,
    columnPrimaryExt: `Primary ext`,
    columnUser: `User`,
    columnModel: `Model`,
    columnMacAddress: `MAC address`,
    columnFirmware: `Firmware`,
    columnWarranty: `Warranty`,
    columnLastCheckIn: `Last check in`,
    columnInitializedOn: `Initialized on`,
    columnConfiguration: `Configuration`,
    columnActions: `Actions`,
    selectAllDevices: `Select all devices`,
  },

  // ---------------------------------------------------------------------------
  // Device row
  // ---------------------------------------------------------------------------
  row: {
    loadingDevices: `Loading devices...`,
    noDevices: `No devices`,
    noDevicesDescription: `There are no devices for this PBX yet.`,
    selectDevice: `Select {{name}}`,
    editDevice: `Edit {{name}}`,
    modelFallback: `Model {{id}}`,
    reportsModel: `Reports: {{model}}`,
    warrantyInWarranty: `In warranty`,
    warrantyExpired: `Expired`,
    warrantyUnknown: `No warranty`,
    changesPending: `Changes Pending`,
    sidecar: `Sidecar`,
    noProv: `No Prov`,
    fromSource: `from {{source}}`,
    desiredFirmware: `(desired: {{version}})`,
    firmwareUnknown: `unknown`,
    rowActions: `Row actions`,
    cloneAction: `Clone`,
    deleteAction: `Delete`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    make: `Make`,
    model: `Model`,
    firmware: `Firmware`,
    firmwarePlaceholder: `Any`,
    warranty: `Warranty`,
    transport: `Transport`,
    jitter: `Jitter`,
    sidecar: `Sidecar`,
    provisioning: `Provisioning`,
    clearFilters: `Clear filters`,
    warrantyUnknown: `No warranty`,
    warrantyInWarranty: `In warranty`,
    warrantyExpired: `Expired`,
    provisioningEnabled: `Enabled`,
    provisioningDisabled: `Disabled`,
    provisioningChangesPending: `Changes pending`,
    toggleFilters: `Toggle filters`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `Results per page:`,
    totalCount: `({{count}} total)`,
    previousPage: `Previous page`,
    nextPage: `Next page`,
    all: `All`,
    jumpToPage: `Jump to page {{page}}`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deviceCreated: `Device created`,
    createFailed: `Failed to create device`,
    deviceUpdated: `Device updated`,
    deviceUpdatedRebooting: `Device updated and rebooting`,
    updateFailed: `Failed to update device`,
  },
} as const satisfies DevicesLocale
